import { useCallback } from 'react';
import type { SelectionEvent } from '@/view/fleet/selection/types';
import { useSelectionEvents } from '@/view/fleet/selection/useSelectionEvents';
import type { ConversationBaseDto, ConversationDto } from './api.types';
import { useChatbot } from './useChatbot';
import { useChatbotHistory } from './useChatbotHistory';
import { useConversation } from './useConversation';

export type ConversationAction =
  | { type: 'none' }
  | { type: 'assign'; conversation: ConversationDto }
  | { type: 'createNew' };

/**
 * If equipment selection is updated, and the selection does not match
 * the current chatbot conversation, initiate a new conversation or
 * select the recent conversation for the selected equipment.
 * The purpose of this is to avoid confusion about which equipment the
 * user is currently chatting about.
 */
export const useWatchEquipmentSelection = () => {
  const { conversation, createNew, assignConversation } = useChatbot();
  const { history } = useChatbotHistory();
  const { recent } = useConversation();

  const onSelect = useCallback(
    (event: SelectionEvent) => {
      const selectedIds = event.detail.selectedIds;

      const action = determineConversationAction({
        selectedIds,
        currentConversation: conversation,
        history,
        recent,
      });

      switch (action.type) {
        case 'assign':
          assignConversation(action.conversation);
          break;
        case 'createNew':
          createNew();
          break;
        case 'none':
          break;
      }
    },
    [conversation, createNew, assignConversation, history, recent]
  );

  useSelectionEvents({ domain: 'fleet-equipment', onSelect });
};

export interface DetermineConversationActionParams {
  selectedIds: string[];
  currentConversation?: ConversationBaseDto;
  history: ConversationDto[];
  recent: Record<string, string>;
}

export const determineConversationAction = ({
  selectedIds,
  currentConversation,
  history,
  recent,
}: DetermineConversationActionParams): ConversationAction => {
  // 1. Do nothing if there are no equipment selected.
  if (selectedIds.length === 0) {
    return { type: 'none' };
  }

  // 2. Do nothing if the active conversation matches the selected equipment.
  if (
    selectedIds.length === 1 &&
    currentConversation?.equipmentId === selectedIds[0]
  ) {
    return { type: 'none' };
  }

  // 3. If we have a new selection of a single equipment, and this equipment has
  // a recent conversation, use that conversation as the new active conversation.
  if (selectedIds.length === 1 && recent[selectedIds[0]]) {
    const recentConversation = history.find(
      (c) => c.id === recent[selectedIds[0]]
    );
    if (recentConversation) {
      return { type: 'assign', conversation: recentConversation };
    }
  }

  // 4. Create a new conversation if we have no matching selection
  // for current or recent conversations.
  return { type: 'createNew' };
};
